/**
 * THE CO-SUBSTRATE ARROW, which biochemistry writes as an arc across the reaction.
 *
 * ── WHAT IT SAYS THAT `->[CO2][-H+]` DOES NOT ────────────────────────────────────────────────
 * Nothing, in information. A labelled arrow already carries what goes in and what comes out,
 * and the owner's own reading of the RuBisCO figure was that the curve is PRESENTATION. What
 * it adds is that the co-substrate JOINS the flow rather than sitting over it as a condition:
 * a biologist reads the arc as the same reaction, and reads a label over a straight arrow as
 * something the reaction runs under. Two different claims, drawn differently.
 *
 * ── WHY IT CANNOT BE AN mhchem ARROW ─────────────────────────────────────────────────────────
 * mhchem draws arrows and sets labels over and under them; it has no arc and no way to add
 * one. So this is Agavi's own syntax, spliced in as a drawing — exactly what `[smiles:…]`
 * already does, and `reaction-compose` already walks the tokens and lays drawings beside
 * written chemistry. Nothing new in the architecture, one more thing in the walk.
 *
 * ── AND WHY IT IS NOT A `DrawnArrow` ─────────────────────────────────────────────────────────
 * Every arrow in that family shares one 14-unit box, which is what makes their strokes match.
 * The arc needs room above AND below the shaft, so it would either be clipped or force the box
 * taller for all nine. Its own box, its own module, and the shared stroke width quoted from
 * there so the two still read as one notation.
 */

/** The same stroke every drawn arrow uses, so the arc does not read as a different hand. */
export const CO_STROKE = 1.3;

/** The box the arc is drawn in. Wider and taller than an arrow's, because the arc needs it. */
export const CO_BOX = { width: 38, height: 22 } as const;

/** The shaft's height within the box: the arrow sits on the text's line, the arc reaches past. */
const SHAFT_Y = 11;

/**
 * The shaft, and the arc that crosses it.
 *
 * The arc enters from above on the left, bends down THROUGH the shaft and leaves below on the
 * right — one continuous sweep, which is what says the co-substrate joins this reaction rather
 * than two unrelated things happening near it. Its head is at the bottom, because the end of
 * the sweep is what leaves.
 *
 * SHALLOWER than it first was. The original depth was partly making room for the two species
 * at the arc's ends, and they turned out to be sitting ON the sweep rather than beside it —
 * they have rows of their own now, so the arc only has to be an arc.
 *
 * Geometry kept as one named list so the shape can be tuned without hunting through markup.
 */
const SHAFT =
  `M2 ${SHAFT_Y} H34 M34 ${SHAFT_Y} L31.2 ${SHAFT_Y - 2.4} M34 ${SHAFT_Y} L31.2 ${SHAFT_Y + 2.4}`;

/** The half that comes IN: down from above on the left, into the shaft. */
const JOINS = `M9 2 C 9 7, 15 ${SHAFT_Y}, 19 ${SHAFT_Y}`;

/** The half that LEAVES: out of the shaft and down on the right, with its head. */
const LEAVES = `M19 ${SHAFT_Y} C 23 ${SHAFT_Y}, 29 15, 29 20`;
const LEAVES_HEAD = `M29 20 L27.1 17.4 M29 20 L30.9 17.4`;

const PATHS: readonly string[] = [
  SHAFT,
  /*
   * The sweep: down at the left, a brief run WITH the shaft, away at the right.
   *
   * The shared run is short on purpose. Tangent control points on both sides made the arc lie
   * along the arrow for a third of its length and the shaft read as doubled; no shared run at
   * all makes two things crossing, which is the one reading this notation is not — the
   * co-substrate joins the flow. A kiss of about four units says joined without thickening it.
   */
  JOINS,
  LEAVES,
  LEAVES_HEAD,
];

/**
 * WHICH HALVES TO DRAW, because it is not always both.
 *
 * Plenty of biochemistry shows a co-substrate going in with nothing named coming out, or a
 * product leaving with nothing named going in — the owner asked, and the answer is no. Drawing
 * a sweep that leaves when nothing leaves is a claim about the reaction, so the arc shows the
 * half that is used. With neither named it is a plain arrow, which is what it then is.
 *
 * The palette KEY passes nothing and gets both, because the icon has to say what the key makes.
 */
function pathsFor(sides?: { into: string; out: string }): readonly string[] {
  if (!sides) return PATHS;
  const joins = !!sides.into.trim();
  const leaves = !!sides.out.trim();
  if (!joins && !leaves) return [SHAFT];
  return [SHAFT, ...(joins ? [JOINS] : []), ...(leaves ? [LEAVES, LEAVES_HEAD] : [])];
}

/** The arc as markup, for the node view and for the composed render alike. */
export function coSubstrateMarkup(em = 2.1, sides?: { into: string; out: string }): string {
  const d = pathsFor(sides).map(p => `<path d="${p}"/>`).join('');
  const height = (em * CO_BOX.height) / CO_BOX.width;
  return `<svg aria-hidden="true" viewBox="0 0 ${CO_BOX.width} ${CO_BOX.height}"`
    + ` class="r-cosub-svg" style="width:${em.toFixed(3)}em;height:${height.toFixed(3)}em"`
    + ` fill="none" stroke="currentColor" stroke-width="${CO_STROKE}"`
    + ` stroke-linecap="round" stroke-linejoin="round">${d}</svg>`;
}

/**
 * ── THE STORED FORM ──────────────────────────────────────────────────────────────────────────
 *
 * `[cosub:<arrow>|<in>|<out>]`, flat rather than nested.
 *
 * The obvious spelling was `[cosub:->[CO2][-H+]]`, wrapping the mhchem it replaces — and it is
 * the wrong one: the brackets nest, so every reader of this string would need a brace matcher
 * to find its end, and the one thing this syntax must be is easy to walk past. Three fields
 * and two pipes is what `[smiles:…|…|…]` already does.
 */
const OPEN = '[cosub:';

export type CoSubstrate = { arrow: string; into: string; out: string };

export const writeCoSubstrate = (c: CoSubstrate): string =>
  `${OPEN}${c.arrow}|${c.into}|${c.out}]`;

/** Read one at `from`, or null. Returns where it ends, so a walker can carry on past it. */
export function readCoSubstrate(src: string, from: number): (CoSubstrate & { end: number }) | null {
  if (!src.startsWith(OPEN, from)) return null;
  const close = src.indexOf(']', from + OPEN.length);
  if (close < 0) return null;
  const [arrow = '', into = '', out = ''] = src.slice(from + OPEN.length, close).split('|');
  if (!arrow) return null;
  return { arrow, into, out, end: close + 1 };
}

/** Cheap enough to ask of every reaction before any work is done. */
export const hasCoSubstrate = (source: string): boolean => source.includes(OPEN);
